// apps/web/src/features/russia-requests/components/editor/RussiaEditorTotalsCard.tsx
//
// COMPLEX request-level totals block for the Russia editor (#1546). A
// COMPLEX request carries a single total weight (kg) / volume (m3)
// instead of per-position values — captured here and bound to the RHF
// form's `totalWeight` / `totalVolume` fields so the values flow through
// the normal autosave create+patch with no separate endpoint. Mirrors the
// China `DraftStandardTotalsCard` field semantics + the #1437 decimal-safe
// text buffer (`useDecimalTextInput`).

import { Controller, type ControllerRenderProps, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreateRussiaRequestManualInput } from '@magimp/shared';
import { useDecimalTextInput } from '../../../requests/draft-media/index.js';
import { Card, Field, inputStyle } from './EditorPrimitives.js';

export interface RussiaEditorTotalsCardProps {
  form: UseFormReturn<CreateRussiaRequestManualInput>;
  onChanged: () => void;
}

type TotalsFieldName = 'totalWeight' | 'totalVolume';

function DecimalTotalsInput({
  field,
  label,
  testId,
  onChanged,
}: {
  field: ControllerRenderProps<CreateRussiaRequestManualInput, TotalsFieldName>;
  label: string;
  testId: string;
  onChanged: () => void;
}) {
  // WHY (#1437): bind the local decimal buffer to the RHF value via the
  //   shared hook — NEVER bind the input directly to a parsed number.
  //   `parseDecimalInput` returns 0 for an empty buffer; map 0 → null so a
  //   cleared field stores the "not set" sentinel the COMPLEX send-readiness
  //   gate expects, instead of a literal 0.
  const buffer = useDecimalTextInput(field.value ?? null, (next) => {
    field.onChange(next === 0 ? null : next);
    onChanged();
  });
  return (
    <Field label={label}>
      <input
        type="text"
        inputMode="decimal"
        data-testid={testId}
        aria-label={label}
        style={inputStyle}
        placeholder="0"
        value={buffer.text}
        onChange={(e) => buffer.onChange(e.target.value)}
        onFocus={buffer.onFocus}
        onBlur={buffer.onBlur}
      />
    </Field>
  );
}

export function RussiaEditorTotalsCard({ form, onChanged }: RussiaEditorTotalsCardProps) {
  const { t } = useTranslation('russiaRequests');
  const { control } = form;
  return (
    <Card title={t('editor.totals.title')}>
      <div data-testid="russia-requests-editor-totals">
        <Controller
          control={control}
          name="totalWeight"
          render={({ field }) => (
            <DecimalTotalsInput
              field={field}
              label={t('editor.totals.weight')}
              testId="russia-requests-editor-totalWeight"
              onChanged={onChanged}
            />
          )}
        />
        <Controller
          control={control}
          name="totalVolume"
          render={({ field }) => (
            <DecimalTotalsInput
              field={field}
              label={t('editor.totals.volume')}
              testId="russia-requests-editor-totalVolume"
              onChanged={onChanged}
            />
          )}
        />
      </div>
    </Card>
  );
}
